// Unified Firebase service that works seamlessly in both Electron and web environments
import isElectron, { isWeb } from '../Utils/isElectron';

class UnifiedFirebaseService {
  constructor() {
    this.environment = isElectron() ? 'electron' : 'web';
    console.log(`[UnifiedFirebaseService] Initialized in ${this.environment} environment`);
  }

  // === DOCUMENT OPERATIONS ===
  
  async addDoc(collectionName, data) {
    try {
      if (isElectron()) {
        console.log('[UnifiedFirebaseService] Using Electron API for addDoc');
        const result = await window.electronAPI.addFirestoreDoc(collectionName, data);
        if (!result.success) {
          throw new Error(result.error || 'Failed to add document');
        }
        return { id: result.docId };
      } else {
        console.log('[UnifiedFirebaseService] Using web Firebase SDK for addDoc');
        const { collection, addDoc, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const docRef = await addDoc(collection(getFirestore(), collectionName), data);
        return { id: docRef.id };
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error adding document:', error);
      throw error;
    }
  }

  async updateDoc(collectionName, docId, data) {
    try {
      if (isElectron()) {
        console.log('[UnifiedFirebaseService] Using Electron API for updateDoc');
        const result = await window.electronAPI.updateFirestoreDoc(collectionName, docId, data);
        if (!result.success) {
          throw new Error(result.error || 'Failed to update document');
        }
        return result;
      } else {
        console.log('[UnifiedFirebaseService] Using web Firebase SDK for updateDoc');
        const { doc, updateDoc, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const docRef = doc(getFirestore(), collectionName, docId);
        await updateDoc(docRef, data);
        return { success: true };
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error updating document:', error);
      throw error;
    }
  }

  async deleteDoc(collectionName, docId) {
    try {
      if (isElectron()) {
        console.log('[UnifiedFirebaseService] Using Electron API for deleteDoc');
        const result = await window.electronAPI.deleteFirestoreDoc(collectionName, docId);
        if (!result.success) {
          throw new Error(result.error || 'Failed to delete document');
        }
        return result;
      } else {
        console.log('[UnifiedFirebaseService] Using web Firebase SDK for deleteDoc');
        const { doc, deleteDoc, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const docRef = doc(getFirestore(), collectionName, docId);
        await deleteDoc(docRef);
        return { success: true };
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error deleting document:', error);
      throw error;
    }
  }

  async queryDoc(collectionName, field, value) {
    try {
      if (isElectron()) {
        console.log('[UnifiedFirebaseService] Using Electron API for queryDoc');
        console.log('[UnifiedFirebaseService] Query params:', { collectionName, field, value });
        
        if (!window.electronAPI || !window.electronAPI.queryFirestoreDoc) {
          throw new Error('Electron API queryFirestoreDoc method not available');
        }
        
        const result = await window.electronAPI.queryFirestoreDoc(collectionName, field, value);
        console.log('[UnifiedFirebaseService] Electron API result:', result);
        
        if (!result) {
          throw new Error('No result returned from Electron API');
        }
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to query document');
        }
        
        // Handle the case when no document is found
        if (result.found === false) {
          console.log('[UnifiedFirebaseService] No document found, returning empty array');
          return { success: true, data: [] };
        }
        
        // Ensure the result has the expected structure
        if (!result.data || !Array.isArray(result.data)) {
          console.error('[UnifiedFirebaseService] Unexpected result structure:', result);
          // Try to normalize the result if possible
          if (result.documents && Array.isArray(result.documents)) {
            result.data = result.documents;
          } else if (result.docs && Array.isArray(result.docs)) {
            result.data = result.docs;
          } else {
            throw new Error('Invalid result structure from Electron API');
          }
        }
        
        return result;
      } else {
        console.log('[UnifiedFirebaseService] Using web Firebase SDK for queryDoc');
        const { collection, query, where, getDocs, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const q = query(collection(getFirestore(), collectionName), where(field, '==', value));
        const querySnapshot = await getDocs(q);
        
        const docs = [];
        querySnapshot.forEach((docSnap) => {
          // Preserve both Firestore doc ID and custom 'id' field from data
          docs.push({ firestoreId: docSnap.id, ...docSnap.data() });
        });
        
        return { success: true, data: docs };
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error querying document:', error);
      throw error;
    }
  }

  // === REAL-TIME LISTENERS ===
  
  startEventsListener(collection, startDate, endDate) {
    if (isElectron()) {
      console.log('[UnifiedFirebaseService] Starting Electron events listener');
      return window.electronAPI.startEventsListener(collection, startDate, endDate);
    } else {
      console.log('[UnifiedFirebaseService] Starting web Firebase events listener');
      // Web implementation would go here if needed
      return null;
    }
  }

  onEventsUpdate(handler) {
    if (isElectron()) {
      console.log('[UnifiedFirebaseService] Setting up Electron events update handler');
      return window.electronAPI.onEventsUpdate(handler);
    } else {
      console.log('[UnifiedFirebaseService] Setting up web Firebase events update handler');
      // Web implementation would go here if needed
      return null;
    }
  }

  // === USER SETTINGS ===
  
  async getUserSettings(userEmail) {
    try {
      if (isElectron()) {
        const result = await window.electronAPI.getUserSettings(userEmail);
        if (result.success) {
          return result.data;
        } else {
          throw new Error(result.error);
        }
      } else {
        const { doc, getDoc, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const userSettingsDocRef = doc(getFirestore(), "user-settings", userEmail);
        const userSettingsDoc = await getDoc(userSettingsDocRef);
        return userSettingsDoc.exists() ? userSettingsDoc.data() : null;
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error getting user settings:', error);
      throw error;
    }
  }

  async updateUserSettings(userEmail, settings) {
    try {
      if (isElectron()) {
        const result = await window.electronAPI.updateUserSettings(userEmail, settings);
        if (!result.success) {
          throw new Error(result.error);
        }
        return result;
      } else {
        const { doc, updateDoc, getFirestore } = await import('firebase/firestore');
        await import('../Utils/firebaseConfig');
        
        const userDocRef = doc(getFirestore(), "user-settings", userEmail);
        await updateDoc(userDocRef, settings);
        return { success: true };
      }
    } catch (error) {
      console.error('[UnifiedFirebaseService] Error updating user settings:', error);
      throw error;
    }
  }

  // === UTILITY METHODS ===
  
  getEnvironment() {
    return this.environment;
  }

  isElectron() {
    return isElectron();
  }

  isWeb() {
    return isWeb();
  }
}

// Export a singleton instance
export const unifiedFirebaseService = new UnifiedFirebaseService();

// Export the class for testing or custom instances
export default UnifiedFirebaseService;
